import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    getAllShipments,
    getShipmentById,
    createShipment,
    updateShipment,
    deleteShipment,
    getShipmentHistory
} from "../../services/ShipmentService";
import { ShipmentDetail, ShipmentStatus, ShipmentFilter, ShipmentHistoryItem } from "../../types/shipment.types";

interface ShipmentState {
    shipmentList: ShipmentDetail[];
    selectedShipment: ShipmentDetail | null;
    loading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
    };
    filters: ShipmentFilter;
    inventoryCount: number; // Total boxes in system
    shipmentHistory: ShipmentHistoryItem[];
    historyLoading: boolean;
}

const initialState: ShipmentState = {
    shipmentList: [],
    selectedShipment: null,
    loading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 50,
        total: 0
    },
    filters: {
        sortBy: 'createdAt',
        sortOrder: 'desc'
    },
    inventoryCount: 0,
    shipmentHistory: [],
    historyLoading: false
};

// Helper function to calculate inventory count
const calculateInventoryCount = (shipments: ShipmentDetail[]): number => {
    return shipments.reduce((count, shipment) => {
        if ([ShipmentStatus.SHIPPED, ShipmentStatus.DELIVERED].includes(shipment.status)) {
            return count + shipment.orderedBoxes;
        }
        return count;
    }, 0);
};

export const shipmentSlice = createSlice({
    name: "shipment",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setFilters: (state, action: PayloadAction<Partial<ShipmentFilter>>) => {
            state.filters = {
                ...state.filters,
                ...action.payload
            };

            // Reset to first page when filters change
            if (Object.keys(action.payload).some(key => key !== 'page')) {
                state.pagination.page = 1;
            }
        },
        clearFilters: (state) => {
            state.filters = {
                sortBy: 'createdAt',
                sortOrder: 'desc'
            };
            state.pagination.page = 1;
        },
        setShipment: (state, action: PayloadAction<ShipmentDetail>) => {
            state.selectedShipment = action.payload;
        },
        clearSelectedShipment: (state) => {
            state.selectedShipment = null;
        },
        clearShipmentHistory: (state) => {
            state.shipmentHistory = [];
            state.historyLoading = false;
        }
    },
    extraReducers: (builder) => {
        // Get All Shipments
        builder
            .addCase(getAllShipments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllShipments.fulfilled, (state, action) => {
                state.loading = false;
                state.shipmentList = action.payload.shipments;
                state.pagination = action.payload.pagination;

                // Calculate inventory count
                state.inventoryCount = calculateInventoryCount(action.payload.shipments);
            })
            .addCase(getAllShipments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch shipments";
            })

            // Get Shipment By Id
            .addCase(getShipmentById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getShipmentById.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedShipment = action.payload;
            })
            .addCase(getShipmentById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch shipment details";
            })

            // Create Shipment
            .addCase(createShipment.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createShipment.fulfilled, (state, action) => {
                state.loading = false;
                // Add new shipment to the list
                state.shipmentList = [action.payload, ...state.shipmentList];
                state.selectedShipment = action.payload;

                // Recalculate inventory if status qualifies
                if ([ShipmentStatus.SHIPPED, ShipmentStatus.DELIVERED].includes(action.payload.status)) {
                    state.inventoryCount += action.payload.orderedBoxes;
                }

                // Update total count
                state.pagination.total += 1;
            })
            .addCase(createShipment.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to create shipment";
            })

            // Update Shipment
            .addCase(updateShipment.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateShipment.fulfilled, (state, action) => {
                state.loading = false;
                // Update shipment in list
                state.shipmentList = state.shipmentList.map(shipment =>
                    shipment.id === action.payload.id ? action.payload : shipment
                );

                // Update selected shipment if it's the same
                if (state.selectedShipment?.id === action.payload.id) {
                    state.selectedShipment = action.payload;
                }

                // Recalculate inventory count since status might have changed
                state.inventoryCount = calculateInventoryCount(state.shipmentList);
            })
            .addCase(updateShipment.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to update shipment";
            })

            // Delete Shipment
            .addCase(deleteShipment.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteShipment.fulfilled, (state, action) => {
                state.loading = false;
                // Find shipment before removing it
                const deletedShipment = state.shipmentList.find(
                    shipment => shipment.id === action.payload
                );

                // Remove from list
                state.shipmentList = state.shipmentList.filter(
                    shipment => shipment.id !== action.payload
                );

                // Clear selected shipment if it was deleted
                if (state.selectedShipment?.id === action.payload) {
                    state.selectedShipment = null;
                }

                // Update inventory count
                if (deletedShipment &&
                    [ShipmentStatus.SHIPPED, ShipmentStatus.DELIVERED].includes(deletedShipment.status)) {
                    state.inventoryCount -= deletedShipment.orderedBoxes;
                }

                // Update total count
                state.pagination.total -= 1;
            })
            .addCase(deleteShipment.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to delete shipment";
            })

            // Get Shipment History
            .addCase(getShipmentHistory.pending, (state) => {
                state.historyLoading = true;
            })
            .addCase(getShipmentHistory.fulfilled, (state, action) => {
                state.historyLoading = false;
                state.shipmentHistory = action.payload;
            })
            .addCase(getShipmentHistory.rejected, (state) => {
                state.historyLoading = false;
                state.shipmentHistory = [];
            });
    }
});

export const {
    clearError,
    setFilters,
    clearFilters,
    setShipment,
    clearSelectedShipment,
    clearShipmentHistory
} = shipmentSlice.actions;

export default shipmentSlice.reducer;
